import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  attachStreamToAudio,
  classifyPlaybackFailure,
  destroyStreamPlayback,
  setMediaSessionPlaybackState,
  updateMediaSession,
} from '../utils/studioRadioPlayback';
import { registerRadioClick } from '../utils/radioBrowserApi';
import { appendTurnstileToken } from '../utils/turnstile.js';

const STORAGE_KEY = 'studio-radio-player-v1';
const CHANNEL_NAME = 'studio-radio-player';

const StudioRadioPlayerContext = createContext(null);

function readStoredState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.station?.url) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredState(station, playing) {
  try {
    if (!station) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        station,
        playing: Boolean(playing),
        updatedAt: Date.now(),
      }),
    );
  } catch {
    /* quota / private mode */
  }
}

export function StudioRadioPlayerProvider({ children }) {
  const audioRef = useRef(null);
  const channelRef = useRef(null);
  const tabIdRef = useRef(`tab-${Math.random().toString(36).slice(2)}`);
  const ignoreBroadcastRef = useRef(false);
  const endedListenersRef = useRef(new Set());

  const [station, setStation] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const broadcast = useCallback((message) => {
    channelRef.current?.postMessage({ ...message, tabId: tabIdRef.current });
  }, []);

  const pauseLocal = useCallback(() => {
    const audio = audioRef.current;
    if (audio) audio.pause();
    destroyStreamPlayback();
    setPlaying(false);
    setMediaSessionPlaybackState(false);
  }, []);

  const stop = useCallback(() => {
    pauseLocal();
    setStation(null);
    setError('');
    writeStoredState(null, false);
    broadcast({ type: 'STOP' });
  }, [broadcast, pauseLocal]);

  const playMedia = useCallback(
    async (nextStation) => {
      if (!nextStation?.url) return;
      setError('');
      setLoading(true);
      setStation(nextStation);
      writeStoredState(nextStation, true);
      if (nextStation.kind !== 'track' && nextStation.uuid) {
        registerRadioClick(nextStation.uuid);
      }

      const audio = audioRef.current;
      if (!audio) {
        setLoading(false);
        return;
      }

      try {
        let streamUrl = nextStation.url;
        if (streamUrl.includes('/api/radio-stream')) {
          streamUrl = await appendTurnstileToken(streamUrl);
        }
        await attachStreamToAudio(audio, streamUrl, { station: nextStation });
        updateMediaSession(nextStation, {
          onPlay: () => audio.play().catch(() => {}),
          onPause: () => pauseLocal(),
          onStop: () => stop(),
        });
        if (audio.paused) {
          await audio.play();
        }
        setPlaying(true);
        setMediaSessionPlaybackState(true);
        ignoreBroadcastRef.current = true;
        broadcast({ type: 'PLAY', station: nextStation });
        ignoreBroadcastRef.current = false;
      } catch (err) {
        setPlaying(false);
        setError(classifyPlaybackFailure(err, audio));
        setMediaSessionPlaybackState(false);
      } finally {
        setLoading(false);
      }
    },
    [broadcast, pauseLocal, stop],
  );

  const playStation = useCallback(
    async (nextStation) => playMedia({ ...nextStation, kind: 'radio' }),
    [playMedia],
  );

  const playTrack = useCallback(
    async ({ trackId, name, artist, album, url, audioKey, sourceTab = 'collection' }) =>
      playMedia({
        kind: 'track',
        trackId,
        name,
        artist,
        album,
        url,
        audioKey,
        sourceTab,
      }),
    [playMedia],
  );

  const registerEndedListener = useCallback((listener) => {
    endedListenersRef.current.add(listener);
    return () => {
      endedListenersRef.current.delete(listener);
    };
  }, []);

  const toggle = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !station) return;
    setError('');
    if (playing) {
      pauseLocal();
      broadcast({ type: 'PAUSE' });
      writeStoredState(station, false);
      return;
    }
    try {
      if (!audio.src && !hlsAttached(audio)) {
        let streamUrl = station.url;
        if (streamUrl.includes('/api/radio-stream')) {
          streamUrl = await appendTurnstileToken(streamUrl);
        }
        await attachStreamToAudio(audio, streamUrl, { station });
      }
      await audio.play();
      setPlaying(true);
      setMediaSessionPlaybackState(true);
      writeStoredState(station, true);
      broadcast({ type: 'RESUME', station });
    } catch (err) {
      setError(classifyPlaybackFailure(err, audio));
    }
  }, [broadcast, pauseLocal, playing, station]);

  function hlsAttached(audio) {
    return Boolean(audio?.srcObject || audio?.currentSrc);
  }

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (event) => {
      const msg = event.data || {};
      if (msg.tabId === tabIdRef.current || ignoreBroadcastRef.current) return;

      if (msg.type === 'PLAY' && msg.station) {
        pauseLocal();
        setStation(msg.station);
        setPlaying(false);
      } else if (msg.type === 'PAUSE') {
        pauseLocal();
        setPlaying(false);
      } else if (msg.type === 'STOP') {
        pauseLocal();
        setStation(null);
        setPlaying(false);
      } else if (msg.type === 'RESUME') {
        pauseLocal();
        setPlaying(false);
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [pauseLocal, station]);

  useEffect(() => {
    const stored = readStoredState();
    if (stored?.station) {
      setStation(stored.station);
      setPlaying(false);
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onPlay = () => setPlaying(true);
    const onPause = () => {
      setPlaying(false);
      writeStoredState(station, false);
    };
    const onError = () => {
      setPlaying(false);
      setError(classifyPlaybackFailure(null, audio));
    };
    const onEnded = () => {
      if (station?.kind !== 'track') return;
      for (const listener of endedListenersRef.current) {
        listener(station);
      }
    };
    const onTimeUpdate = () => setCurrentTime(audio.currentTime || 0);
    const syncDuration = () => {
      const next = Number.isFinite(audio.duration) ? audio.duration : 0;
      setDuration(next > 0 ? next : 0);
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('error', onError);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', syncDuration);
    audio.addEventListener('durationchange', syncDuration);
    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('error', onError);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', syncDuration);
      audio.removeEventListener('durationchange', syncDuration);
    };
  }, [station]);

  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
  }, [station?.url, station?.trackId]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.register('/studio-radio-sw.js').catch(() => {});
  }, []);

  const value = useMemo(
    () => ({
      station,
      playing,
      loading,
      error,
      currentTime,
      duration,
      playStation,
      playTrack,
      toggle,
      stop,
      setError,
      registerEndedListener,
    }),
    [
      station,
      playing,
      loading,
      error,
      currentTime,
      duration,
      playStation,
      playTrack,
      toggle,
      stop,
      registerEndedListener,
    ],
  );

  return (
    <StudioRadioPlayerContext.Provider value={value}>
      {children}
      <audio ref={audioRef} preload="none" playsInline disableRemotePlayback />
    </StudioRadioPlayerContext.Provider>
  );
}

export function useStudioRadioPlayer() {
  const ctx = useContext(StudioRadioPlayerContext);
  if (!ctx) {
    throw new Error('useStudioRadioPlayer must be used within StudioRadioPlayerProvider');
  }
  return ctx;
}

export function useStudioRadioPlayerOptional() {
  return useContext(StudioRadioPlayerContext);
}
